import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {BalanceEntry} from "../../../payment-plan/model/balance-entry.interface";
import {BmDebtCollectionService} from "../../service/bm-debt-collection.service";
import {DunningData} from "../../../payment-plan/model/dunning-data.interface";

@Component({
    selector: 'bm-debt-collection-balance',
    templateUrl: './bm-debt-collection-balance.component.html',
    styleUrls: ['./bm-debt-collection-balance.component.scss'],
    standalone: false
})
export class BmDebtCollectionBalanceComponent implements OnInit, OnDestroy {


    balanceEntries: BalanceEntry[] = [];
    manualPayments: BalanceEntry[] = [];
    totalBalance: number = 0;
    contractsTotalBalance: number = 0;
    manualPaymentsTotalBalance: number = 0;
    dunningData: DunningData | null = null;
    showManualPaymentDialog: boolean = false;
    private destroy$ = new Subject<void>();

    constructor(private debtCollectionService: BmDebtCollectionService) {
    }

    ngOnInit(): void {
        this.subscribeToBalanceChanges();
        this.subscribeToDunningDataChanges();
    }

    /**
     * Subscribe to balance entries changes
     */
    private subscribeToBalanceChanges(): void {
        this.debtCollectionService.balanceEntries$
            .pipe(takeUntil(this.destroy$))
            .subscribe(entries => {
                if (entries?.length > 0) {
                    this.balanceEntries = entries.filter(entry => entry.active && entry.type != 'one-time-payment');
                    this.manualPayments = entries.filter(entry => entry.active && entry.type == 'one-time-payment');
                    this.calculateTotalBalance();
                }
            });

    }

    private subscribeToDunningDataChanges(): void {
        this.debtCollectionService.dunningData$
            .pipe(takeUntil(this.destroy$))
            .subscribe(entries => {
                this.dunningData = entries;
                this.calculateContractsTotal();
            }, (error) => {
                this.dunningData = null;
            });
    }

    /**
     * Calculate total balance from all entries
     */
    private calculateTotalBalance(): void {
        this.totalBalance = this.balanceEntries.reduce(
            (sum, entry) => sum + (entry.active ? entry.amount : 0),
            0
        );
        this.manualPaymentsTotalBalance = this.manualPayments.reduce((sum, entry) => sum + entry.amount, 0);
        this.calculateContractsTotal();
    }

    private calculateContractsTotal() {
        if (this.dunningData && this.dunningData?.contracts) {
            const { contracts } = this.dunningData;
            if (contracts.length === 0) {
                this.contractsTotalBalance = 0;
                return;
            }

            const totalDunningAmount = contracts.reduce((sum, { dunningAmount }) => sum + dunningAmount, 0);
            this.contractsTotalBalance = totalDunningAmount - this.totalBalance - this.manualPaymentsTotalBalance;
        }
    }

    /**
     * Remove a balance entry
     */
    async removeEntry(entry: BalanceEntry): Promise<void> {
        if (!entry.removable) {
            return;
        }

        // Determine which service method to call based on entry type
        if (entry.type === 'one-time-payment') {
            await this.debtCollectionService.removeOneTimePayment(entry.name);
        } else if (entry.type === 'contract-dunning' || entry.type === 'contract-reactivation') {
            // Extract contract identifier from entry id
            const contractIdentifier = entry.id.replace('contract-', '');
            await this.debtCollectionService.removeContractActivity(contractIdentifier);
        } else if (entry.type === 'charge') {
            // For charges, we need to get the original charge and restore it
            const dunningData = this.debtCollectionService.getDunningData();
            if (dunningData) {
                const charge = dunningData.charges.find(c => c.name === entry.name);
                if (charge) {
                    await this.debtCollectionService.adjustCharge(charge, 'remove');
                }
            }
        }
    }

    openManualPaymentDialog() {
        this.showManualPaymentDialog = true;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
